// XMind 檔案匯入匯出。支援 XMind 2020+（content.json）與 XMind 8（content.xml）。
// 對應不到的功能（摘要、浮動主題、多主題外框、未知標記）會以診斷訊息回報，不會默默遺失。
import { type Diagnostic, type MindMap, type Relation, type Structure, type Topic, hash32, walk } from './model.js';
import { ICON_NAMES } from './icons.js';
import { type Inflate, readZip, writeZip } from './zip.js';
import { type XmlElement, parseXml, child, childrenNamed } from './xml-parse.js';

/** content.json 與 content.xml 共用的中間格式 */
interface XTopic {
  id?: string;
  title?: string;
  structureClass?: string;
  branch?: string;
  children?: { attached?: XTopic[]; detached?: XTopic[]; summary?: XTopic[] };
  markers?: { markerId?: string }[];
  notes?: { plain?: { content?: string } };
  boundaries?: { range?: string; title?: string }[];
  labels?: string[];
}

interface XSheet {
  title?: string;
  rootTopic?: XTopic;
  relationships?: { end1Id?: string; end2Id?: string; title?: string }[];
}

export interface XmindSheet {
  title: string;
  map: MindMap;
  diagnostics: Diagnostic[];
}

// XMind 標記 → Sind 圖示（依前綴比對）。
const MARKER_PREFIXES: [RegExp, string][] = [
  [/^task-done$/, 'check'],
  [/^task-/, 'clock'],
  [/^priority-/, 'flag'],
  [/^flag-/, 'flag'],
  [/^star-/, 'star'],
  [/^people-/, 'people'],
  [/^smiley-/, 'heart'],
  [/question/, 'question'],
  [/exclam|bomb/, 'warning'],
  [/wrong|-no$/, 'cross'],
  [/right|-yes$/, 'check'],
  [/info/, 'info'],
  [/lightbulb|idea/, 'idea'],
  [/calendar/, 'calendar'],
  [/clock|alarm/, 'clock'],
  [/money|dollar/, 'money'],
  [/book/, 'book'],
  [/lock/, 'lock'],
  [/mail|email/, 'mail'],
  [/heart/, 'heart'],
  [/chart/, 'chart'],
  [/gear|setting/, 'gear'],
  [/pin/, 'pin'],
];

// Sind 圖示 → XMind 標記（只列 XMind 內建、確定存在的標記；其他圖示改存成標籤）。
const ICON_MARKERS: Record<string, string> = {
  check: 'task-done', clock: 'task-start', flag: 'flag-red', star: 'star-red', people: 'people-red',
  question: 'symbol-question', warning: 'symbol-exclam', cross: 'symbol-wrong',
};

const STRUCTURE_CLASSES: Record<Structure, string> = {
  balanced: 'org.xmind.ui.map.clockwise',
  'logic-right': 'org.xmind.ui.logic.right',
  'logic-left': 'org.xmind.ui.logic.left',
  org: 'org.xmind.ui.org-chart.down',
};

// XMind 結構類別 → Sind 結構；不支援的回傳 null。
function structureFrom(structureClass: string | undefined): Structure | null {
  if (!structureClass) return 'balanced';
  if (/logic\.right|tree\.right/.test(structureClass)) return 'logic-right';
  if (/logic\.left|tree\.left/.test(structureClass)) return 'logic-left';
  if (/org-chart\.down/.test(structureClass)) return 'org';
  if (/\.map(\.|$)/.test(structureClass)) return 'balanced';
  return null;
}

// XMind 8 的 XML 主題元素 → 中間格式。
function topicFromXml(element: XmlElement): XTopic {
  const children = child(element, 'children');
  const group = (type: string) => childrenNamed(children, 'topics').filter(topics => topics.attrs.type === type).flatMap(topics => childrenNamed(topics, 'topic').map(topicFromXml));
  return {
    id: element.attrs.id,
    title: child(element, 'title')?.text ?? '',
    structureClass: element.attrs['structure-class'],
    branch: element.attrs.branch,
    children: { attached: group('attached'), detached: group('detached'), summary: group('summary') },
    markers: childrenNamed(child(element, 'marker-refs'), 'marker-ref').map(ref => ({ markerId: ref.attrs['marker-id'] })),
    notes: { plain: { content: child(child(element, 'notes'), 'plain')?.text ?? '' } },
    boundaries: childrenNamed(child(element, 'boundaries'), 'boundary').map(boundary => ({ range: boundary.attrs.range, title: child(boundary, 'title')?.text })),
    labels: childrenNamed(child(element, 'labels'), 'label').map(label => label.text),
  };
}

// XMind 8 content.xml → 工作表清單。
function sheetsFromXml(xml: string): XSheet[] {
  const root = parseXml(xml);
  return childrenNamed(root, 'sheet').map(sheet => {
    const topic = child(sheet, 'topic');
    return {
      title: child(sheet, 'title')?.text,
      ...(topic ? { rootTopic: topicFromXml(topic) } : {}),
      relationships: childrenNamed(child(sheet, 'relationships'), 'relationship').map(relation => ({
        end1Id: relation.attrs.end1, end2Id: relation.attrs.end2, title: child(relation, 'title')?.text,
      })),
    };
  });
}

// 中間格式 → MindMap。
function convertSheet(sheet: XSheet, index: number): XmindSheet {
  const diagnostics: Diagnostic[] = [];
  const warn = (message: string) => diagnostics.push({ line: 0, severity: 'warning', message });
  const usedIds = new Set<string>();
  const idMap = new Map<string, string>();
  const counts = { detached: 0, summary: 0, unknownMarkers: new Set<string>(), multiBoundary: 0 };

  const makeId = (source: string | undefined, seed: string): string => {
    let id = source && /^[\w-]{1,64}$/.test(source) && !usedIds.has(source) ? source : '';
    for (let attempt = 0; !id || usedIds.has(id); attempt++) id = 't' + hash32(`${seed}#${attempt}`).toString(36);
    usedIds.add(id);
    if (source) idMap.set(source, id);
    return id;
  };

  const convert = (input: XTopic, seed: string): Topic => {
    const title = (input.title ?? '').replace(/\s+/g, ' ').trim() || '(untitled)';
    const topic: Topic = { id: makeId(input.id, `${seed}/${title}`), title, children: [] };
    const note = input.notes?.plain?.content?.trim();
    // Sind 匯出時把沒有對應標記的圖示存成標籤；匯入時還原。其他標籤併入備註。
    const labels = (input.labels ?? []).filter(Boolean);
    const iconLabel = labels.find(label => ICON_NAMES.includes(label));
    if (iconLabel) topic.icon = iconLabel;
    const otherLabels = labels.filter(label => label !== iconLabel);
    const noteParts = [note, otherLabels.length ? `labels: ${otherLabels.join(', ')}` : ''].filter(Boolean);
    if (noteParts.length) topic.note = noteParts.join('\n');
    for (const marker of input.markers ?? []) {
      const markerId = marker.markerId ?? '';
      const icon = MARKER_PREFIXES.find(([pattern]) => pattern.test(markerId))?.[1];
      if (icon && !topic.icon) topic.icon = icon;
      else if (!icon && markerId) counts.unknownMarkers.add(markerId);
    }
    if (input.branch === 'folded') topic.collapsed = true;
    const attached = input.children?.attached ?? [];
    topic.children = attached.map((childTopic, childIndex) => convert(childTopic, `${topic.id}/${childIndex}`));
    // 浮動主題接到中心主題下，避免遺失。
    const detached = input.children?.detached ?? [];
    if (detached.length) {
      counts.detached += detached.length;
      topic.children.push(...detached.map((childTopic, childIndex) => convert(childTopic, `${topic.id}/d${childIndex}`)));
    }
    counts.summary += input.children?.summary?.length ?? 0;
    // 外框：range "(i,j)" 指子主題範圍；"master" 指主題本身。
    for (const boundary of input.boundaries ?? []) {
      const label = (boundary.title ?? '').trim();
      const range = /\((\d+),(\d+)\)/.exec(boundary.range ?? '');
      if (!range) { topic.boundary = label; continue; }
      const from = Number(range[1]);
      const to = Number(range[2]);
      if (to > from) counts.multiBoundary++;
      for (let position = from; position <= to; position++) {
        const target = topic.children[position];
        if (target) target.boundary = label;
      }
    }
    return topic;
  };

  const rootInput = sheet.rootTopic ?? { title: sheet.title ?? `Sheet ${index + 1}` };
  const root = convert(rootInput, `sheet${index}`);
  delete root.boundary;
  let structure = structureFrom(rootInput.structureClass);
  if (!structure) { warn(`XMind 結構「${rootInput.structureClass}」不支援，已改為平衡圖`); structure = 'balanced'; }

  const relations: Relation[] = [];
  for (const relationship of sheet.relationships ?? []) {
    const from = idMap.get(relationship.end1Id ?? '');
    const to = idMap.get(relationship.end2Id ?? '');
    if (!from || !to || from === to) { warn('有關聯線連到不存在的主題，已略過'); continue; }
    const label = relationship.title?.trim();
    relations.push({ from, to, ...(label ? { label } : {}) });
  }

  if (counts.detached) warn(`${counts.detached} 個浮動主題已接到中心主題底下`);
  if (counts.summary) warn(`${counts.summary} 個摘要（summary）主題無法對應，已略過`);
  if (counts.multiBoundary) warn(`${counts.multiBoundary} 個外框涵蓋多個主題，已拆成每個主題各一框`);
  if (counts.unknownMarkers.size) warn(`無對應圖示的標記已略過：${[...counts.unknownMarkers].join(', ')}`);
  return { title: (sheet.title ?? '').trim() || root.title, map: { root, relations, structure, theme: 'sketch' }, diagnostics };
}

// 匯入 .xmind 檔（ZIP），回傳所有工作表。
export async function importXmind(bytes: Uint8Array, inflate: Inflate): Promise<XmindSheet[]> {
  const files = await readZip(bytes, inflate);
  const decoder = new TextDecoder();
  let sheets: XSheet[];
  const json = files.get('content.json');
  const xml = files.get('content.xml');
  if (json) {
    const parsed: unknown = JSON.parse(decoder.decode(json));
    if (!Array.isArray(parsed)) throw new Error('content.json 格式不正確');
    sheets = parsed as XSheet[];
  } else if (xml) {
    sheets = sheetsFromXml(decoder.decode(xml));
  } else {
    throw new Error('不是 XMind 檔（找不到 content.json 或 content.xml）');
  }
  if (!sheets.length) throw new Error('XMind 檔沒有任何工作表');
  return sheets.map(convertSheet);
}

// MindMap → XMind 2020+ 格式的 .xmind（不壓縮的 ZIP）。
export function exportXmind(map: MindMap): Uint8Array {
  const parents = new Map<string, Topic>();
  walk(map.root, (topic, _depth, parent) => { if (parent) parents.set(topic.id, parent); });

  const toJson = (topic: Topic, isRoot: boolean): Record<string, unknown> => {
    const result: Record<string, unknown> = { id: topic.id, class: 'topic', title: topic.title };
    if (isRoot) result.structureClass = STRUCTURE_CLASSES[map.structure];
    if (topic.children.length) result.children = { attached: topic.children.map(childTopic => toJson(childTopic, false)) };
    if (topic.note) result.notes = { plain: { content: topic.note } };
    if (topic.collapsed) result.branch = 'folded';
    if (topic.icon) {
      const marker = ICON_MARKERS[topic.icon];
      if (marker) result.markers = [{ markerId: marker }];
      else if (ICON_NAMES.includes(topic.icon)) result.labels = [topic.icon];
    }
    // 子主題的外框掛在本主題上，以範圍 (i,i) 表示。
    const boundaries = topic.children.flatMap((childTopic, index) => childTopic.boundary !== undefined
      ? [{ id: `${childTopic.id}-boundary`, range: `(${index},${index})`, ...(childTopic.boundary ? { title: childTopic.boundary } : {}) }]
      : []);
    if (boundaries.length) result.boundaries = boundaries;
    return result;
  };

  const content = [{
    id: `${map.root.id}-sheet`,
    class: 'sheet',
    title: map.root.title,
    rootTopic: toJson(map.root, true),
    ...(map.relations.length ? {
      relationships: map.relations.map((relation, index) => ({
        id: `rel-${index}-${relation.from}`, end1Id: relation.from, end2Id: relation.to, ...(relation.label ? { title: relation.label } : {}),
      })),
    } : {}),
  }];
  const encoder = new TextEncoder();
  return writeZip([
    { name: 'content.json', data: encoder.encode(JSON.stringify(content)) },
    { name: 'metadata.json', data: encoder.encode(JSON.stringify({ creator: { name: 'Sind', version: '0.1.0' } })) },
    { name: 'manifest.json', data: encoder.encode(JSON.stringify({ 'file-entries': { 'content.json': {}, 'metadata.json': {} } })) },
  ]);
}
